"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "./AnimatedSection";

export default function FaqSection() {
  const { t } = useLang();

  const faqs = [
    {
      q: { en: "Will my customers be upset about dual pricing?", es: "¿Mis clientes se molestarán por el dual pricing?" },
      a: {
        en: "Most customers already know it from gas stations. The menu shows the card price, and customers who pay cash get a discount — shown as its own \"Cash discount\" line on the receipt.",
        es: "La mayoría de los clientes ya lo conoce de las gasolineras. El menú muestra el precio con tarjeta, y quien paga en efectivo recibe un descuento — que aparece como su propia línea de \"Descuento en efectivo\" en el recibo.",
      },
    },
    {
      q: { en: "Is dual pricing allowed?", es: "¿El dual pricing está permitido?" },
      a: {
        en: "Redy uses the cash-discount model: the menu shows the card price, and customers who pay cash get a discount. Many businesses, like gas stations, work this way. Rules vary by state and card network, and you're responsible for the signage and disclosures your state requires — ask us or your payment processor if you're unsure.",
        es: "Redy usa el modelo de descuento en efectivo: el menú muestra el precio con tarjeta, y quien paga en efectivo recibe un descuento. Muchos negocios, como las gasolineras, funcionan así. Las reglas cambian según el estado y la red de tarjetas, y tú eres responsable de los avisos que exige tu estado — pregúntanos a nosotros o a tu procesador de pagos si tienes dudas.",
      },
    },
    {
      q: { en: "What hardware do I need?", es: "¿Qué hardware necesito?" },
      a: {
        en: "Two things: an iPad or Android tablet (which you likely already have) and a North payment terminal. We guide you through ordering the terminal after signup. You own it.",
        es: "Dos cosas: un iPad o tablet Android (que ya tienes) y un terminal de pagos de North. Te guiamos para ordenarlo después de registrarte. El equipo es tuyo.",
      },
    },
    {
      q: { en: "What if I'm in a contract with my current POS?", es: "¿Qué pasa si tengo un contrato con mi POS actual?" },
      a: {
        en: "You can still sign up. We'll help you figure out the timeline for your current contract and plan the switch so there's no disruption.",
        es: "Puedes registrarte igual. Te ayudaremos a planear la transición de tu contrato actual para que no haya interrupciones en tu negocio.",
      },
    },
    {
      q: { en: "How is event ticketing priced?", es: "¿Cuánto cuesta el ticketing de eventos?" },
      a: {
        en: "Many ticketing platforms add a fee to every ticket. Redy charges no platform fee per ticket — Redy Nightlife PRO is a flat $197/month, and dual pricing means card payers cover the processing cost.",
        es: "Muchas plataformas de ticketing agregan un fee a cada boleto. Redy no cobra fee de plataforma por boleto — Redy Nightlife PRO es un flat de $197/mes, y con dual pricing quien paga con tarjeta cubre el costo de procesamiento.",
      },
    },
    {
      q: { en: "How do promoter commissions work?", es: "¿Cómo funcionan las comisiones de promotores?" },
      a: {
        en: "With Nightlife PRO, each promoter gets their own tracked link per event. Every sale through it is attributed automatically, and Redy calculates the commission — flat per ticket or a percentage. You see sales and commission per promoter, pay them your way, and mark the commission paid in Redy.",
        es: "Con Nightlife PRO, cada promotor tiene su propio link rastreado por evento. Cada venta por ese link se atribuye sola, y Redy calcula la comisión — fija por entrada o un porcentaje. Tú ves ventas y comisión por promotor, les pagas a tu manera y marcas la comisión como pagada en Redy.",
      },
    },
  ];

  return (
    <section id="faq" className="py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto text-center">
        <AnimatedSection className="mb-20">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">FAQ</div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal">
            {t("Questions We", "Preguntas Que")}
            <br />
            <span className="text-black/30">
              {t("Get A Lot", "Nos Hacen Seguido")}
            </span>
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left max-w-[1000px] mx-auto">
          {faqs.map((faq, i) => (
            <AnimatedElement key={i} delay={0.1 * i} className="bg-[#FAFAFA] border border-black/5 rounded-[32px] p-8 hover:shadow-lg transition-all group cursor-pointer">
              <h3 className="text-[16px] font-bold text-redy-charcoal mb-4 pr-6 group-hover:text-redy-orange transition-colors">
                {t(faq.q.en, faq.q.es)}
              </h3>
              <p className="text-[14px] font-medium text-redy-secondary leading-relaxed">
                {t(faq.a.en, faq.a.es)}
              </p>
            </AnimatedElement>
          ))}
        </div>

        <AnimatedSection delay={0.4} className="mt-16 flex flex-col items-center gap-4">
          <p className="text-[14px] font-medium text-redy-secondary">
            {t("Still have questions? We'll answer them personally.", "¿Más preguntas? Te las respondemos en persona.")}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 text-[14px] font-bold text-white bg-redy-charcoal px-8 py-3.5 rounded-full hover:bg-black transition-all active:scale-95 shadow-sm cursor-pointer"
            >
              {t("Get started free", "Comenzar gratis")}
              <span className="text-lg leading-none">→</span>
            </button>
            <a
              href="mailto:hello@useredy.com"
              className="inline-flex items-center text-[14px] font-bold text-redy-charcoal px-6 py-3.5 rounded-full border border-black/15 bg-white hover:bg-[#F0EDE8] transition-all shadow-sm cursor-pointer"
            >
              {t("Talk to a human", "Hablar con alguien")}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
